var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { formatCurrency, triggerAnalyticsEvent } from 'syf-js-utilities';
import VerticalCardLoader from 'syf-component-library/ui/atoms/Loader/Skeleton/VerticalCardLoader';
import { Inset, Stack } from 'syf-component-library/ui/spacing';
import { ModalOptions } from '../../../../typings/common';
import useProfileQuery from '../../../../services/profile/profile-query';
import { StyledBorder, StyledContainer, StyledFlex } from '../../../../styles/common';
import { accountIdType } from '../../../../const/config';
import { FA_DOLLAR_SIGN, FA_HOUSE } from '../../../../const/icon-props';
import { StyledText } from '../../../../styles/text';
import { colors } from '../../../../styles/theme';
import useDeviceWidth from '../../../../helpers/use-device-width';
import Icon from '../../../icon';
import CBRContext from '../../context';
import RefundRequestBanner from './refundRequestBanner';
import { DetailDescription, DetailValue, TextButton } from './subcomponents';
import { AnalyticsValues, TriggerAnalyticsKeys, manageProfileLinkDataProps } from '../../../../const/analytics';
import { CardContainer } from '../../eligibility/subcomponents';
import routes from '../../../../const/routes';
import store from '../../../../helpers/store';
import queryParamsToObject from '../../credit-balance-refund-card/queryParamsToObject';
import { STORE_KEYS, STORE_LOCATIONS } from '../../../../const/store-keys';
var RefundRequestDetails = function (_a) {
    var authenticationRedirect = _a.authenticationRedirect, currentBalance = _a.currentBalance, setCurrentDrawer = _a.setCurrentDrawer, _b = _a.isSuccessModal, isSuccessModal = _b === void 0 ? false : _b;
    var _c = useState(false), redirectEnabled = _c[0], setRedirectEnabled = _c[1];
    var t = useTranslation().t;
    var isMobile = useDeviceWidth();
    var _d = useContext(CBRContext), accountId = _d.accountId, config = _d.config, handleProfileClick = _d.handleProfileClick, setIsLoading = _d.setIsLoading;
    var _e = useProfileQuery({
        config: config,
        keyProps: {
            accountId: accountId,
            accountIdType: accountIdType
        },
        queryOptions: {
            enabled: Boolean(accountId),
            staleTime: Infinity
        }
    }), profileData = _e.data, isLoading = _e.isLoading, isFetching = _e.isFetching, isRefetching = _e.isRefetching;
    var isDataNotReady = isFetching || isLoading || isRefetching;
    var scope = 'read write write:sensitive profile:read:sensitive';
    var statusCode = profileData === null || profileData === void 0 ? void 0 : profileData.statusCode;
    useEffect(function () {
        var _a, _b, _c;
        setIsLoading === null || setIsLoading === void 0 ? void 0 : setIsLoading(true);
        var baseValues = (_a = {},
            _a[TriggerAnalyticsKeys.pageKind] = AnalyticsValues.cbr,
            _a[TriggerAnalyticsKeys.pageFunction] = AnalyticsValues.manageCreditBalance,
            _a);
        if (!isSuccessModal && !isDataNotReady && accountId) {
            triggerAnalyticsEvent(__assign(__assign({}, baseValues), (_b = {}, _b[TriggerAnalyticsKeys.pageName] = AnalyticsValues.review, _b)));
        }
        if (isSuccessModal && accountId) {
            triggerAnalyticsEvent(__assign(__assign({}, baseValues), (_c = {}, _c[TriggerAnalyticsKeys.pageName] = AnalyticsValues.submitted, _c[TriggerAnalyticsKeys.message] = AnalyticsValues.refundSubmitted, _c)));
        }
        if (!isDataNotReady && statusCode !== 403) {
            setIsLoading === null || setIsLoading === void 0 ? void 0 : setIsLoading(false);
        }
    }, [accountId, isDataNotReady, isSuccessModal, setIsLoading, statusCode]);
    useEffect(function () {
        var otpErrorCallbackParams = store.get(STORE_KEYS.otpErrorCallbackParams, STORE_LOCATIONS.session);
        var otpErrorExists = Boolean(otpErrorCallbackParams);
        /*
         * * sets redirectEnable to false if there are errors from OTP flow
         * * if has OtpErrors, setCurrentDrawer to Default (Drawer Options screen)
         * * if has no OtpErrors, we can redirect to otpFlow
         */
        setRedirectEnabled(!otpErrorExists);
        if (otpErrorExists) {
            var errorObjectParams = queryParamsToObject(otpErrorCallbackParams);
            var hasError = !!(errorObjectParams === null || errorObjectParams === void 0 ? void 0 : errorObjectParams.error);
            if (hasError) {
                setCurrentDrawer === null || setCurrentDrawer === void 0 ? void 0 : setCurrentDrawer(ModalOptions.default);
                setIsLoading === null || setIsLoading === void 0 ? void 0 : setIsLoading(false);
            }
        }
    }, [setCurrentDrawer, setIsLoading]);
    if (isDataNotReady) {
        return (_jsx(Inset, { children: _jsx(VerticalCardLoader, {}) }));
    }
    if (statusCode === 403 && redirectEnabled) {
        authenticationRedirect({
            scope: scope,
            navigateTo: routes.HOME,
            modal: 'CreditBalanceRefund'
        });
    }
    if (statusCode === 403 || isDataNotReady) {
        return (_jsx(CardContainer, __assign({ "data-testid": "loader" }, { children: _jsx(VerticalCardLoader, {}) })));
    }
    var handleProfileLinkClick = function () {
        if (handleProfileClick) {
            handleProfileClick(accountId);
        }
    };
    if (!(profileData === null || profileData === void 0 ? void 0 : profileData.profile)) {
        return null;
    }
    var _f = profileData.profile, firstName = _f["cipher.firstName"], lastName = _f["cipher.lastName"], _g = _f.mailingAddress, address1 = _g["cipher.addressLine1"], address2 = _g["cipher.addressLine2"], city = _g["cipher.city"], state = _g["cipher.state"], zipCode = _g["cipher.zipCode"];
    var fullName = "".concat(firstName, " ").concat(lastName);
    var cityStateZip = "".concat(city, ", ").concat(state, ", ").concat(zipCode);
    var manageProfileLinkText = t('button.manageProfile');
    var manageProfileLink = (_jsx(TextButton, __assign({ buttonType: "text", onClick: handleProfileLinkClick }, manageProfileLinkDataProps, { children: manageProfileLinkText })));
    return (_jsxs(_Fragment, { children: [!isSuccessModal && _jsx(RefundRequestBanner, {}), _jsxs(Inset, __assign({ horizontal: "xlarge", vertical: isMobile ? 'base' : 'xxlarge' }, { children: [_jsx(StyledText, __assign({ weight: "light", size: "medium" }, { children: t('text.refundRequestDetails') })), _jsx(Stack, {}), _jsx(StyledBorder, {}), _jsx(Inset, __assign({ horizontal: "large" }, { children: _jsxs(StyledFlex, __assign({ direction: "column", justifyContent: "space-between" }, { children: [_jsxs(StyledFlex, __assign({ direction: isMobile ? 'column' : 'row' }, { children: [_jsxs(StyledFlex, __assign({ alignItems: "center" }, { children: [_jsx(Icon, { icon: FA_DOLLAR_SIGN, title: "refund check", size: "1x", color: colors.cyanBlue, width: 20 }), _jsx(Inset, { right: "base" }), _jsx(DetailDescription, { children: _jsx(StyledText, { children: t('text.amount') }) })] })), _jsx(DetailValue, { children: _jsx(StyledText, __assign({ weight: "bold" }, { children: formatCurrency(currentBalance) })) })] })), _jsx(Stack, { size: "large" }), _jsxs(StyledFlex, __assign({ alignItems: "flex-start", direction: isMobile ? 'column' : 'row' }, { children: [_jsxs(StyledFlex, __assign({ alignItems: "center" }, { children: [_jsx(Icon, { icon: FA_HOUSE, title: "address", size: "1x", color: colors.cyanBlue, width: 20 }), _jsx(Inset, { right: "base" }), _jsx(DetailDescription, { children: _jsx(StyledText, { children: t('text.address') }) })] })), _jsxs(DetailValue, __assign({ direction: "column" }, { children: [_jsx(StyledText, __assign({ weight: "bold" }, { children: fullName })), _jsx(StyledText, __assign({ weight: "bold" }, { children: address1 })), address2 && _jsx(StyledText, __assign({ weight: "bold" }, { children: address2 })), _jsx(StyledText, __assign({ weight: "bold" }, { children: cityStateZip }))] }))] }))] })) })), !isSuccessModal && (_jsx(StyledContainer, { children: _jsx(Trans, __assign({ i18nKey: "wrongAddress", value: manageProfileLink, components: {
                                manageProfileLink: manageProfileLink
                            } }, { children: t('text.wrongAddress', { manageProfileLinkText: manageProfileLinkText }) })) }))] }))] }));
};
export default RefundRequestDetails;
